<script setup lang="ts">
import type { HTMLAttributes } from 'vue';
import { computed } from 'vue';

interface Props {
  // 方向：水平或垂直
  orientation?: 'horizontal' | 'vertical';
  // 样式变体
  variant?: 'solid' | 'text';
  // 文本内容（仅在 variant 为 text 时有效）
  text?: string;
  // 文本位置（仅在 variant 为 text 时有效）
  textAlign?: 'left' | 'center' | 'right';
  // 自定义类名
  class?: HTMLAttributes['class'];
  // 自定义样式
  style?: HTMLAttributes['style'];
}

const props = withDefaults(defineProps<Props>(), {
  orientation: 'horizontal',
  variant: 'solid',
  textAlign: 'center',
});

// 计算样式类名
const dividerClasses = computed(() => [
  'divider',
  `divider--${props.orientation}`,
  `divider--${props.variant}`,
  'divider--default',
  props.variant === 'text' && `divider--x-text-${props.textAlign}`,
  props.class,
]);

// 计算内联样式
const dividerStyle = computed(() => {
  const baseStyle = props.style || {};
  return baseStyle;
});
</script>

<template>
  <div
    v-if="variant === 'text' && text"
    :class="dividerClasses"
    :style="dividerStyle"
    role="separator"
    :aria-orientation="orientation"
  >
    <span class="divider__text">
      <slot name="icon"></slot>
      <span class="flex-1">{{ text }}</span>
    </span>
  </div>

  <div
    v-else
    :class="dividerClasses"
    :style="dividerStyle"
    role="separator"
    :aria-orientation="orientation"
  />
</template>

<style scoped lang="scss">
/* 基础样式 */
.divider {
  display: flex;
  align-items: center;
  position: relative;
  box-sizing: border-box;
  border-width: 0;
}

/* 水平分割线 */
.divider--horizontal {
  width: 100%;
  height: 0;
  border-top: 1px solid;
  height: 18px;
}

/* 垂直分割线 */
.divider--vertical {
  width: 0px;
  background-color: var(--x-back-4);
  margin: 0 12px;
  border-left: 1px solid;
  align-self: stretch;
}

/* 样式变体 */
.divider--solid {
  border-style: solid;
}

.divider--text {
  border: none;
  background: none;
}

.divider--text::before,
.divider--text::after {
  content: '';
  flex: 1;
  height: 1px;
  background-color: var(--x-back-4);
}

/* 文本分割线样式 */
.divider--text .divider__text {
  padding: 0 12px;
  font-size: 12px;
  color: inherit;
  white-space: nowrap;
  display: flex;
  align-items: center;
}

/* 文本对齐方式 */
.divider--x-text-left::before {
  flex: 0 0 20%;
}

.divider--x-text-left::after {
  flex: 1;
}

.divider--x-text-right::before {
  flex: 1;
}

.divider--x-text-right::after {
  flex: 0 0 20%;
}

.divider--x-text-center::before,
.divider--x-text-center::after {
  flex: 1;
}

/* 颜色变体 */
.divider--default {
  color: var(--x-text-7);
  border-color: var(--x-back-4);
}

/* 工具类 */
.divider--no-margin {
  margin: 0;
}

.divider--full-width {
  width: 100%;
}

.divider--full-height {
  height: 100%;
}
</style>
